/**
 * **Bring something back?** (TMMAPP-0153 item 9; prototype P:756–773, state 12; visual contract v2
 * §10): taking something from a breakout to its shared session. The room tabs are how the person
 * returns without sharing (TMMAPP-0178 item 3).
 *
 * - "Your breakout conversation stays private. Only what you choose below is added to “{shared
 *   session}”."
 * - **A note from you**: prefilled with the room's latest reply draft (`replyDraft.text`), or the
 *   draft whose Post opened it (item 10), else empty.
 * - **Cancel** closes the dialog and stays in the room; nothing is posted.
 * - **Share & return** posts the note as the person's own message (the page's `onShare`: `POST
 *   …/sessions/{sharedId}/messages`, `askAi: false`), then goes there. It's disabled while the note is
 *   empty, and one press posts once.
 * - **The shared session can't take messages** (archived, or the person's access there ended):
 *   Share is hidden and the dialog says "{shared session} can't take new messages." A post refused
 *   for that reason turns the dialog to that state, and the note stays in the box (0148 review L3).
 * - Not built: the prototype's "Include the assistant's numbers summary" (no API posts an assistant
 *   summary, and posting the assistant's words as the person's would misattribute them).
 */
import { useId, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { resolveTokens } from '@/theme/theme';

const NOTE_MAX_LENGTH = 4000;

interface BringBackDialogProps {
  sharedName: string;
  /** The note it opens with (a reply draft's text), else empty. */
  initialNote: string;
  /** The shared session takes new messages; false hides Share. */
  writable: boolean;
  /** Posts the note as the person's own message to the shared session; rejects when refused. */
  onShare: (note: string) => Promise<void>;
  /** Cancel, Escape or the backdrop: the room stays open. */
  onClose: () => void;
}

export function BringBackDialog({
  sharedName,
  initialNote,
  writable,
  onShare,
  onClose,
}: BringBackDialogProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const titleId = useId();
  const bodyId = useId();
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [note, setNote] = useState(initialNote);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Read-only while the shared session can't take it, computed every render, never seeded once from
  // the prop (TMMAPP-0154 item 25): served as not writable, or refused here. Share goes.
  const [refusedLocally, setRefusedLocally] = useState(false);
  const closed = refusedLocally || !writable;
  const noteId = useId();
  const { dirty } = useDirty(note);
  const guard = useDialogDirtyGuard(dirty, onClose, busy);
  const trimmed = note.trim();

  async function share() {
    if (trimmed.length === 0 || busy || closed) return;
    setBusy(true);
    setError(null);
    try {
      await onShare(trimmed);
    } catch (caught) {
      if (
        isApiError(caught) &&
        (caught.code === ErrorCodes.ASSISTANT_SESSION_ARCHIVED ||
          caught.code === ErrorCodes.ASSISTANT_ACCESS_ENDED)
      ) {
        setRefusedLocally(true);
      } else {
        setError(errorCopy(t, caught, 'assistant.bringBack.shareError'));
        guard.focusErrors();
      }
      setBusy(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      fullWidth
      maxWidth="sm"
      subtitle={
        <DialogSubtitle id={bodyId}>{t('assistant.bringBack.body', { sharedName })}</DialogSubtitle>
      }
      subtitleId={bodyId}
      slotProps={{
        paper: { sx: { maxWidth: 520 } },
        transition: { onEntered: () => noteRef.current?.focus() },
      }}
      data-bring-back=""
    >
      <DialogTitle id={titleId}>{t('assistant.bringBack.title')}</DialogTitle>
      <DialogContent>
        {closed && (
          <Alert severity="info" sx={{ mt: 1 }} data-bring-back-closed="">
            {t('assistant.bringBack.readOnly', { sharedName })}
          </Alert>
        )}
        {error !== null && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {/* The label sits above the field, 13 / 600 in `text2`, not floating (contract v2 §10). */}
        <Typography
          component="label"
          htmlFor={noteId}
          data-bring-back-label=""
          sx={{
            display: 'block',
            mt: 1.5,
            mb: 0.75,
            fontSize: 13,
            lineHeight: '18px',
            fontWeight: 600,
            color: tokens.text2,
          }}
        >
          {t('assistant.bringBack.note')}
        </Typography>
        <TextField
          id={noteId}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          inputRef={noteRef}
          multiline
          minRows={3}
          fullWidth
          disabled={busy}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: tokens.surface2 } }}
          slotProps={{ htmlInput: { maxLength: NOTE_MAX_LENGTH } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        {!closed && (
          <SubmitButton
            disabled={trimmed.length === 0}
            loading={busy}
            onClick={() => void share()}
            data-share-return=""
          >
            {t('assistant.bringBack.share')}
          </SubmitButton>
        )}
      </DialogActions>
    </GuardedDialog>
  );
}
